// Mock implementation of /api/secure/me/sessions/* — wire shape mirrors the
// Java DTOs (SessionSummary, SessionDetail, etc.) exactly.
//
// Profile-aware: SELF profile gets a richer therapy history; family profile
// gets a chronic-condition flow. The mock store is mutable so reschedule/
// cancel/feedback round-trips feel real for the F3 demo.

import { delay } from './index'

const STORAGE_KEY = 'patientPortal.mock.sessions.v1'

function nowPlusMin(min) {
  return new Date(Date.now() + min * 60 * 1000).toISOString()
}
function daysAgo(days) {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
}

function seedFor(profile) {
  if (!profile) return { upcoming: [], past: [], detail: {}, feedback: {}, requests: [] }
  const isSelf = profile.relation === 'SELF'

  const upcomingSelf = [
    {
      eventId: 'evt_self_today',
      // /l/{slug} is the canonical live-session route. Mock seeds keep slug
      // populated so the frontend's slug-first join-URL path is exercisable
      // even on the in-memory store.
      slug: 'meera-therapy-2026-05-05-am',
      practitionerId: 'prac_dr_meera',
      practitionerName: 'Dr. Meera Iyer',
      practitionerSpecialty: 'Therapist',
      sessionType: 'therapy',
      title: 'Therapy session',
      scheduledAt: nowPlusMin(28),
      durationMinutes: 50,
      status: 'SCHEDULED',
      canJoinNow: true,
      canManage: true,
      agendaPrompt: "Let's revisit the sleep hygiene plan and how this week went.",
      joinUrl: '/l/meera-therapy-2026-05-05-am',
    },
    {
      eventId: 'evt_self_tomorrow',
      practitionerId: 'prac_dr_neha',
      practitionerName: 'Dr. Neha Bose',
      practitionerSpecialty: 'Nutritionist',
      sessionType: 'consultation',
      title: 'Nutrition follow-up',
      scheduledAt: nowPlusMin(60 * 22),
      durationMinutes: 30,
      status: 'SCHEDULED',
      canJoinNow: false,
      canManage: true,
    },
    {
      eventId: 'evt_self_next_week',
      practitionerId: 'prac_dr_meera',
      practitionerName: 'Dr. Meera Iyer',
      practitionerSpecialty: 'Therapist',
      sessionType: 'therapy',
      title: 'Therapy session',
      scheduledAt: nowPlusMin(60 * 24 * 7),
      durationMinutes: 50,
      status: 'SCHEDULED',
      canJoinNow: false,
      canManage: true,
    },
  ]

  const pastSelf = [
    {
      eventId: 'evt_self_past_1',
      practitionerId: 'prac_dr_meera',
      practitionerName: 'Dr. Meera Iyer',
      practitionerSpecialty: 'Therapist',
      sessionType: 'therapy',
      title: 'Therapy session',
      scheduledAt: daysAgo(7),
      durationMinutes: 50,
      status: 'COMPLETED',
      canJoinNow: false,
      canManage: false,
      sessionMode: 'ONLINE',
      // Past-session card chips: paid + payment mode + mode + duration.
      // Mocks mirror the backend SessionSummary shape so the chips render
      // without a real backend roundtrip.
      paidAmountMinor: 250000, // ₹2,500
      originalPriceMinor: 250000,
      currency: 'INR',
      paymentMode: 'UPI',
      receiptAvailable: true,
    },
    {
      eventId: 'evt_self_past_2',
      practitionerId: 'prac_dr_neha',
      practitionerName: 'Dr. Neha Bose',
      practitionerSpecialty: 'Nutritionist',
      sessionType: 'consultation',
      title: 'Nutrition consultation',
      scheduledAt: daysAgo(14),
      durationMinutes: 30,
      status: 'COMPLETED',
      canJoinNow: false,
      canManage: false,
      sessionMode: 'ONLINE',
      paidAmountMinor: 100000, // ₹1,000 — discounted from ₹1,500
      originalPriceMinor: 150000,
      currency: 'INR',
      paymentMode: 'Card',
      receiptAvailable: true,
    },
    {
      eventId: 'evt_self_past_3',
      practitionerId: 'prac_dr_meera',
      practitionerName: 'Dr. Meera Iyer',
      practitionerSpecialty: 'Therapist',
      sessionType: 'therapy',
      title: 'Therapy session',
      scheduledAt: daysAgo(21),
      durationMinutes: 50,
      status: 'COMPLETED',
      canJoinNow: false,
      canManage: false,
      sessionMode: 'OFFLINE',
      locationName: 'Aumhum clinic — Indiranagar',
      paidAmountMinor: 250000,
      originalPriceMinor: 250000,
      currency: 'INR',
      paymentMode: 'Cash',
      receiptAvailable: true,
    },
  ]

  const upcomingFamily = [
    {
      eventId: 'evt_mom_next',
      practitionerId: 'prac_dr_rao',
      practitionerName: 'Dr. Rao',
      practitionerSpecialty: 'Endocrinology',
      sessionType: 'consultation',
      title: 'Endocrine follow-up',
      scheduledAt: nowPlusMin(60 * 50),
      durationMinutes: 30,
      status: 'SCHEDULED',
      canJoinNow: false,
      canManage: true,
    },
  ]

  const pastFamily = [
    {
      eventId: 'evt_mom_past_1',
      practitionerId: 'prac_dr_rao',
      practitionerName: 'Dr. Rao',
      practitionerSpecialty: 'Endocrinology',
      sessionType: 'consultation',
      title: 'Endocrine follow-up',
      scheduledAt: daysAgo(30),
      durationMinutes: 30,
      status: 'COMPLETED',
      canJoinNow: false,
      canManage: false,
    },
    {
      eventId: 'evt_mom_past_2',
      practitionerId: 'prac_anjali',
      practitionerName: 'Physio Anjali',
      practitionerSpecialty: 'Physiotherapy',
      sessionType: 'consultation',
      title: 'Physio review',
      scheduledAt: daysAgo(45),
      durationMinutes: 45,
      status: 'COMPLETED',
      canJoinNow: false,
      canManage: false,
    },
  ]

  return {
    upcoming: isSelf ? upcomingSelf : upcomingFamily,
    past: isSelf ? pastSelf : pastFamily,
    detail: {},
    feedback: {},
    requests: [],
  }
}

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch (e) {
    return {}
  }
}
function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch (e) {
    /* ignore */
  }
}

function ensureProfile(profile) {
  const store = readStore()
  if (!store[profile.id]) {
    store[profile.id] = seedFor(profile)
    writeStore(store)
  }
  return { store, prof: store[profile.id] }
}

// --- API surface ---

export async function listSessions(profile, status, page = 1, pageSize = 20) {
  await delay()
  if (!profile) return { items: [], page, pageSize, totalItems: 0, hasMore: false }
  const { prof } = ensureProfile(profile)
  const items = status === 'past' ? prof.past : prof.upcoming
  return { items, page, pageSize, totalItems: items.length, hasMore: false }
}

export async function getSession(profile, eventId) {
  await delay()
  if (!profile) return null
  const { prof } = ensureProfile(profile)
  const summary =
    prof.upcoming.find((s) => s.eventId === eventId) ||
    prof.past.find((s) => s.eventId === eventId)
  if (!summary) return null
  const isPast = summary.status === 'COMPLETED'
  return {
    summary,
    summaryNote: isPast
      ? 'Discussed sleep routine adjustments and continued SSRI taper. Patient reports improved baseline mood.'
      : null,
    prescriptionId: isPast ? `rx_${eventId}` : null,
    receiptId: isPast ? `rcpt_${eventId}` : null,
    sharedNoteIds: isPast ? [`note_${eventId}_1`] : [],
    feedback: prof.feedback[eventId] || null,
    canDownloadReceipt: isPast,
    canViewPrescription: isPast,
    rescheduleDeadline: !isPast ? nowPlusMin(-60) : null,
    cancellationDeadline: !isPast ? nowPlusMin(-120) : null,
  }
}

export async function reschedule(profile, eventId, body) {
  await delay()
  if (!profile) return null
  const { prof } = ensureProfile(profile)
  const idx = prof.upcoming.findIndex((s) => s.eventId === eventId)
  if (idx === -1) throw new Error('Session not found')
  return {
    id: `mock_req_${Date.now()}`,
    actionType: 'RESCHEDULE',
    bookedEventId: eventId,
    practitionerId: prof.upcoming[idx].practitionerId,
    reason: body?.reason || 'Reschedule requested',
    status: 'PENDING',
  }
}

export async function cancel(profile, eventId, body) {
  await delay()
  if (!profile) return null
  const { prof } = ensureProfile(profile)
  const idx = prof.upcoming.findIndex((s) => s.eventId === eventId)
  if (idx === -1) throw new Error('Session not found')
  return {
    id: `mock_req_${Date.now()}`,
    actionType: 'CANCEL',
    bookedEventId: eventId,
    practitionerId: prof.upcoming[idx].practitionerId,
    reason: body?.reason || 'Cancellation requested',
    status: 'PENDING',
  }
}

export async function submitFeedback(profile, eventId, body) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensureProfile(profile)
  const fb = {
    id: `fb_${eventId}`,
    eventId,
    rating: body.rating,
    text: body.text || null,
    submittedAt: new Date().toISOString(),
  }
  prof.feedback[eventId] = fb
  store[profile.id] = prof
  writeStore(store)
  return fb
}

export async function getFeedback(profile, eventId) {
  await delay(80)
  if (!profile) return null
  const { prof } = ensureProfile(profile)
  return prof.feedback[eventId] || null
}

export async function requestSession(profile, body) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensureProfile(profile)
  const req = {
    id: `req_${Date.now()}`,
    practitionerId: body.practitionerId,
    preferredSlots: body.preferredSlots || [],
    reason: body.reason || null,
    desiredSessionType: body.desiredSessionType || null,
    status: 'PENDING',
    createdAt: new Date().toISOString(),
  }
  prof.requests.push(req)
  store[profile.id] = prof
  writeStore(store)
  return req
}

export async function listRequests(profile) {
  await delay(80)
  if (!profile) return []
  const { prof } = ensureProfile(profile)
  return prof.requests
}

export function __resetSessionsMock() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    /* ignore */
  }
}
